/**
 * Shared avatar color palette for the new renter experience.
 *
 * Two sets of WCAG 2.2 AA-compliant colors (verified at 4.5:1 minimum):
 *   Light mode: dark fills — white text passes 4.5:1 against each fill.
 *   Dark mode:  light fills — dark (#212B36) text passes 4.5:1 against each fill.
 *
 * Usage: assign by member index so every person in a list gets a distinct,
 * consistent color regardless of their role.
 */

export const AVATAR_COLORS_LIGHT = [
  '#1A5276', // steel blue  — white on fill: 8.36:1
  '#6C3483', // purple      — white on fill: 8.55:1
  '#922B21', // rose        — white on fill: 8.11:1
  '#784212', // amber       — white on fill: 8.12:1
  '#117A65', // teal        — white on fill: 5.26:1
  '#1E8449', // olive       — white on fill: 4.72:1
];

export const AVATAR_COLORS_DARK = [
  '#85C1E9', // steel blue  — dark text on fill: 7.38:1
  '#C39BD3', // purple      — dark text on fill: 6.12:1
  '#F1948A', // rose        — dark text on fill: 6.38:1
  '#F0B27A', // amber       — dark text on fill: 7.76:1
  '#76D7C4', // teal        — dark text on fill: 8.40:1
  '#82E0AA', // olive       — dark text on fill: 9.01:1
];

/**
 * Returns the fill color and text color for an avatar at a given index.
 * Cycles through the palette if there are more members than palette entries.
 */
export function getIndexedAvatarColors(
  index: number,
  isDark: boolean
): { bg: string; fg: string } {
  const palette = isDark ? AVATAR_COLORS_DARK : AVATAR_COLORS_LIGHT;
  const bg = palette[index % palette.length];
  const fg = isDark ? '#212B36' : '#FFFFFF';
  return { bg, fg };
}

/**
 * Returns the fill color and text color for an avatar keyed by a stable userId.
 * Use this instead of getIndexedAvatarColors when the list may exceed 6 members
 * or when render order is not guaranteed — avoids two users landing on the same
 * color just because their array indices are 6 apart.
 *
 * Spreads userId across the palette using a simple hash so consecutive IDs
 * (e.g. 1 and 7) don't collide.
 */
export function getUserAvatarColors(
  userId: number,
  isDark: boolean
): { bg: string; fg: string } {
  const palette = isDark ? AVATAR_COLORS_DARK : AVATAR_COLORS_LIGHT;
  // Multiplicative hash — distributes consecutive integers across the palette.
  const hash = (userId * 2654435761) >>> 0;
  const bg = palette[hash % palette.length];
  const fg = isDark ? '#212B36' : '#FFFFFF';
  return { bg, fg };
}
